/**
 * lib/ImageCacheContext.tsx
 * --------------------------
 * What: Lightweight image cache/provider used by item views and other components
 * that need predictable image loading across platforms.
 * Why: Centralises caching strategy and avoids repeated fetch logic in screens.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

type CacheEntry = {
  url: string;
  objectUrl?: string; // blob-backed URL for zero-network re-renders
  timestamp: number;
};

type ImageCache = {
  [key: string]: CacheEntry;
};

type ImageCacheContextType = {
  getCachedUrl: (key: string) => string | null;
  setCachedUrl: (key: string, url: string) => void;
  setCachedObjectUrl: (key: string, objectUrl: string) => void;
  clearCache: () => void;
  clearItemCache: (itemId: string) => void;
};

const ImageCacheContext = createContext<ImageCacheContextType | undefined>(undefined);

export function ImageCacheProvider({ children }: { children: React.ReactNode }) {
  const [cache, setCache] = useState<ImageCache>({});

  console.log('🖼️ [ImageCacheProvider] Provider rendered, cache size:', Object.keys(cache).length);

  const getCachedUrl = useCallback((key: string) => {
    console.log('🔍 [ImageCache] getCachedUrl called for key:', key);
    const cached = cache[key];
    if (!cached) {
      console.log('❌ [ImageCache] No cache found for key:', key);
      return null;
    }

    // Check if cache is expired (1 hour)
    const now = Date.now();
    const age = now - cached.timestamp;
    console.log('⏰ [ImageCache] Cache age for key:', key, 'age:', age, 'ms');
    
    if (age > 60 * 60 * 1000) {
      console.log('⏰ [ImageCache] Cache expired for key:', key, 'removing from cache');
      // Remove expired cache
      setCache(prev => {
        const newCache = { ...prev };
        // Revoke any blob URL to avoid leaks
        try {
          if (newCache[key]?.objectUrl) {
            URL.revokeObjectURL(newCache[key].objectUrl!);
          }
        } catch {}
        delete newCache[key];
        console.log('🗑️ [ImageCache] Removed expired cache for key:', key);
        return newCache;
      });
      return null;
    }

    const resolved = cached.objectUrl || cached.url;
    console.log('✅ [ImageCache] Returning cached URL for key:', key, 'blob?', Boolean(cached.objectUrl));
    return resolved;
  }, [cache]);

  const setCachedUrl = useCallback((key: string, url: string) => {
    console.log('💾 [ImageCache] setCachedUrl called for key:', key, 'url length:', url.length);
    setCache(prev => {
      const newCache = {
        ...prev,
        [key]: {
          url,
          objectUrl: prev[key]?.objectUrl, // preserve any blob url
          timestamp: Date.now()
        } as CacheEntry
      };
      console.log('💾 [ImageCache] Cached URL for key:', key, 'new cache size:', Object.keys(newCache).length);
      return newCache;
    });
  }, []);

  const setCachedObjectUrl = useCallback((key: string, objectUrl: string) => {
    console.log('💾 [ImageCache] setCachedObjectUrl called for key:', key);
    setCache(prev => {
      const prevEntry = prev[key];
      // Revoke old object URL if replacing
      if (prevEntry?.objectUrl && prevEntry.objectUrl !== objectUrl) {
        try { URL.revokeObjectURL(prevEntry.objectUrl); } catch {}
      }
      const newCache = {
        ...prev,
        [key]: {
          url: prevEntry?.url || objectUrl,
          objectUrl,
          timestamp: Date.now()
        } as CacheEntry
      };
      return newCache;
    });
  }, []);

  const clearCache = useCallback(() => {
    console.log('🗑️ [ImageCache] clearCache called, clearing all cached images');
    setCache(prev => {
      // Revoke all blob URLs
      Object.values(prev).forEach(entry => {
        try { if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl); } catch {}
      });
      return {};
    });
  }, []);

  const clearItemCache = useCallback((itemId: string) => {
    console.log('🗑️ [ImageCache] clearItemCache called for itemId:', itemId);
    setCache(prev => {
      const newCache = { ...prev };
      let removedCount = 0;
      // Remove all cached images for this item, including those with order information
      Object.keys(newCache).forEach(key => {
        if (key.startsWith(`item-${itemId}-thumb`)) {
          try { if (newCache[key]?.objectUrl) URL.revokeObjectURL(newCache[key].objectUrl!); } catch {}
          delete newCache[key];
          removedCount++;
        }
      });
      console.log('🗑️ [ImageCache] Removed', removedCount, 'cached images for itemId:', itemId);
      return newCache;
    });
  }, []);

  console.log('📤 [ImageCacheProvider] Returning context with cache size:', Object.keys(cache).length);

  return (
    <ImageCacheContext.Provider value={{
      getCachedUrl,
      setCachedUrl,
      setCachedObjectUrl,
      clearCache,
      clearItemCache
    }}>
      {children}
    </ImageCacheContext.Provider>
  );
}

export function useImageCache() {
  const context = useContext(ImageCacheContext);
  if (context === undefined) {
    throw new Error('useImageCache must be used within an ImageCacheProvider');
  }
  console.log('🖼️ [useImageCache] Hook called');
  return context;
} 